import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { X, Mail, Users } from 'lucide-react';
import { PromoCode } from '../../model/professional/promo-code-model';
import companyViewModel from '../../view-model/professional/company/company-view-model';

interface PromoCodeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (clientIds: string[]) => Promise<void>;
  promoCode: PromoCode;
  companyId: string;
}

const PromoCodeEmailModal: React.FC<PromoCodeEmailModalProps> = observer(({
  isOpen,
  onClose,
  onSend,
  promoCode,
  companyId,
}) => {
  const { t } = useTranslation();
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  // Charger les clients de l'entreprise
  useEffect(() => {
    if (isOpen && companyId) {
      companyViewModel.handleGetCompanyClients(companyId);
    }
  }, [isOpen, companyId]);

  // Réinitialiser la sélection à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setSelectedClientIds([]);
    }
  }, [isOpen]);

  const handleToggleClient = (clientId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    setSelectedClientIds(companyViewModel.companyClients.map((c: any) => c.id));
  };

  const handleDeselectAll = () => {
    setSelectedClientIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedClientIds.length === 0) {
      return;
    }

    setIsSending(true);
    try {
      await onSend(selectedClientIds);
      onClose();
    } catch (error) {
      console.error('Error sending emails:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Tous les clients de l'entreprise
  const allClients = companyViewModel.companyClients;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Mail size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('promoCode.sendEmailTitle', 'Envoyer le code promo par email')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Code: <span className="font-mono font-bold text-primary">{promoCode.code}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Info du code promo */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {promoCode.discountType === 'percentage' ? (
                <>
                  -{promoCode.discountValue}% de réduction
                </>
              ) : (
                <>
                  -{promoCode.discountValue}€ de réduction
                </>
              )}
            </div>
            {promoCode.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {promoCode.description}
              </p>
            )}
          </div>

          {/* Sélection des destinataires */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <Users size={18} />
                {t('promoCode.selectRecipients', 'Sélectionner les destinataires')}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-primary hover:underline"
                >
                  {t('common.selectAll', 'Tout sélectionner')}
                </button>
                <span className="text-xs text-gray-400">•</span>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="text-xs text-gray-500 hover:underline"
                >
                  {t('common.deselectAll', 'Tout désélectionner')}
                </button>
              </div>
            </div>

            {companyViewModel.isLoadingClients ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('common.loading', 'Chargement...')}
              </div>
            ) : allClients.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('promoCode.noClients', 'Aucun client n\'a encore réservé de service dans cette entreprise')}
              </div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                {allClients.map((client: any) => (
                  <label
                    key={client.id}
                    className="flex items-center px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(client.id)}
                      onChange={() => handleToggleClient(client.id)}
                      className="mr-2 w-4 h-4 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {client.firstName} {client.lastName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {client.email}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {selectedClientIds.length} {t('promoCode.clientsSelected', 'client(s) sélectionné(s)')}
            </p>
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSending}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSending || selectedClientIds.length === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Mail size={18} />
              {isSending
                ? t('promoCode.sending', 'Envoi en cours...')
                : t('promoCode.sendToClients', `Envoyer à ${selectedClientIds.length} client(s)`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default PromoCodeEmailModal;
